import { createSlice } from '@reduxjs/toolkit';
import { initialMapState } from 'reducers/initialStates';
import { createSelector } from 'reselect';
import { filterMapFeaturesByBoundingBox, emptyFeatureCollection } from 'utils/mapsUtils';

const mapSlice = createSlice({
	name: 'map',
	initialState: {
		//Set by configureStore
	},
	reducers: {
		setMapLoading(state, action) {
			state.mapLoading = action.payload;
		},
		setUserCoordinate(state, action) {
			state.userCoordinate = action.payload;
		},
		setMarkerDetails(state, action) {
			state.markerDetails = action.payload;
		},
		setUserLocationLoading(state, action) {
			state.userLocationLoading = action.payload;
		},
		setLocationDetailsLoading(state, action) {
			state.locationDetailsLoading = action.payload;
		},
		setMapStyle(state, action) {
			state.style = action.payload;
		},
		setMapStyleConfig(state, action) {
			state.styleConfig = action.payload;
		},
		setWeatherLoading(state, action) {
			state.weatherLoading = action.payload;
		},
		setWeather(state, action) {
			state.weather = action.payload;
		},
		setAdvisoriesLoading(state, action) {
			state.advisoriesLoading = action.payload;
		},
		setAdvisoryObject(state, action) {
			state.advisory = action.payload;
		},
		setGeolocationError(state, action) {
			state.geolocationError = action.payload;
		},
		setFeatureCollection(state, action) {
			state.featureCollection = action.payload;
		},
		clearFeatureCollection(state) {
			state.featureCollection = emptyFeatureCollection;
		},
		clearNonPinFeatures(state) {
			state.featureCollection = {
				...state.featureCollection,
				features: state.featureCollection.features.filter(element => element?.properties?.isMapPin)
			};
		},
		clearNonPinAndNonMeasurementFeatures(state) {
			state.featureCollection = {
				...state.featureCollection,
				features: state.featureCollection.features.filter(
					element => element?.properties?.isMapPin || element?.properties?.isMeasurement
				)
			};
		},
		clearMeasurementFeatures(state) {
			state.featureCollection = {
				...state.featureCollection,
				features: state.featureCollection.features.filter(
					element => !element?.properties?.isMeasurement
				)
			};
		},
		setUpdateMapFromFile(state, action) {
			state.updateMapFromFile = action.payload;
		},
		setSelectedFeatures(state, action) {
			state.selectedFeatures = action.payload;
		},
		setRadiusObject(state, action) {
			state.radiusObject = {
				...state.radiusObject,
				...action.payload
			};
		},
		setUnauthenticatedStyle(state, action) {
			state.unauthenticatedStyle = action.payload;
		},
		setMapSources(state, action) {
			state.sources = action.payload;
		},
		setOpsViewMapSources(state, action) {
			state.mapSources = action.payload;
		},
		setVisibleMapLayers(state, action) {
			state.visibleMapLayers = action.payload;
		},
		setMapStyleOptions(state, action) {
			state.styleOptions = action.payload;
		},
		setTileServerLayers(state, action) {
			state.tileServerLayers = action.payload;
		},
		setMapboxSources(state, action) {
			state.mapboxSources = action.payload;
		},
		setTileServerSources(state, action) {
			state.tileServerSources = action.payload;
		},
		setVisibleMapboxSources(state, action) {
			state.visibleMapboxSources = action.payload;
		},
		setMapReadyForDrawing(state, action) {
			state.mapReadyForDrawing = action.payload;
		},
		setAloftSources(state, action) {
			state.aloftSources = action.payload;
		},
		setMapClickedFeatures(state, action) {
			state.clickedFeatures = action.payload;
		},
		setSearchOptions(state, action) {
			state.searchOptions = action.payload;
		},
		setMapBoundingBox(state, action) {
			state.mapBoundingBox = action.payload;
		},
		setDrawInstance(state, action) {
			state.drawInstance = action.payload;
		},

		setRenderMap(state, action) {
			state.renderMap = action.payload;
		},
		setShowFeatureCard(state, action) {
			state.showFeatureCard = action.payload;
		},
		setMapPinInView(state, action) {
			state.mapPinInView = action.payload;
		},
		setAirspaceMapBounds(state, action) {
			state.airspaceMapBounds = action.payload;
		},
		setAirspaceMapPin(state, action) {
			state.airspaceMapPin = action.payload;
		},

		//Start new ops view stuff
		setShowPilotCard(state, action) {
			state.showPilotCard = action.payload;
		},
		setMapLayers(state, action) {
			state.mapLayers = action.payload;
		},
		setMapBasemaps(state, action) {
			state.mapBasemaps = action.payload;
		},
		setMapModifiers(state, action) {
			state.mapModifiers = action.payload;
		},
		setMapSections(state, action) {
			state.mapSections = action.payload;
		},
		setMapRegions(state, action) {
			state.mapRegions = action.payload;
		},
		setGeoJsonData(state, action) {
			state.geoJsonData = action.payload;
		},
		setDefaultBasemap(state, action) {
			state.defaultBasemap = action.payload;
		},
		setSelectedBasemap(state, action) {
			state.basemap = action.payload;
		},

		setSavedMapViews(state, action) {
			state.savedMapViews = action.payload;
		},
		setSavedMapViewsLoading(state, action) {
			state.savedMapViewsLoading = action.payload;
		},
		setClickedAdvisoryId(state, action) {
			state.clickedAdvisoryId = action.payload;
		},
		resetMapSlice() {
			return initialMapState;
		}
	}
});

export const {
	setMapLoading,
	setUserCoordinate,
	setMarkerDetails,
	setUserLocationLoading,
	setLocationDetailsLoading,
	setMapStyle,
	setMapStyleConfig,
	setWeatherLoading,
	setWeather,
	setAdvisoriesLoading,
	setAdvisoryObject,
	setGeolocationError,
	setFeatureCollection,
	clearNonPinFeatures,
	clearNonPinAndNonMeasurementFeatures,
	clearMeasurementFeatures,
	setUpdateMapFromFile,
	setSelectedFeatures,
	setRadiusObject,
	clearFeatureCollection,
	setMapSources,
	setVisibleMapLayers,
	setMapStyleOptions,
	setTileServerLayers,
	setMapboxSources,
	setTileServerSources,
	setVisibleMapboxSources,
	setMapReadyForDrawing,
	setAloftSources,
	resetMapSlice,
	setUnauthenticatedStyle,
	setMapClickedFeatures,
	setSearchOptions,
	setMapBoundingBox,
	setDrawInstance,
	setRenderMap,
	setShowFeatureCard,
	setMapPinInView,
	setAirspaceMapBounds,
	setAirspaceMapPin,
	setSelectedBasemap,
	setShowPilotCard,
	setMapLayers,
	setMapBasemaps,
	setMapModifiers,
	setMapSections,
	setMapRegions,
	setGeoJsonData,
	setDefaultBasemap,
	setOpsViewMapSources,
	setSavedMapViews,
	setSavedMapViewsLoading,
	setClickedAdvisoryId
} = mapSlice.actions;

export default mapSlice.reducer;

export const mapLoadingSelector = state => state.map.mapLoading;
export const userCoordinateSelector = state => state.map.userCoordinate;
export const markerDetailsSelector = state => state.map.markerDetails;
export const userLocationLoadingSelector = state => state.map.userLocationLoading;
export const locationDetailsLoadingSelector = state => state.map.locationDetailsLoading;
export const mapSelectedLayersSelector = state => state.map.selectedLayers;

export const mapStyleSelector = state => state.map.style;
export const mapStyleOptionsSelector = state => state.map.styleOptions;
export const mapStyleConfigSelector = state => state.map.styleConfig;
export const weatherLoadingSelector = state => state.map.weatherLoading;
export const weatherSelector = state => state.map.weather;
export const advisoriesLoadingSelector = state => state.map.advisoriesLoading;
export const advisoryObjectSelector = state => state.map.advisory;
export const geolocationErrorSelector = state => state.map.geolocationError;
export const featureCollectionSelector = state => state.map.featureCollection;
export const updateMapFromFileSelector = state => state.map.updateMapFromFile;
export const drawInstanceSelector = state => state.map.drawInstance;
export const selectedFeaturesSelector = state => state.map.selectedFeatures;
export const radiusObjectSelector = state => state.map.radiusObject;
export const visibleMapLayersSelector = state => state.map.visibleMapLayers;
export const tileServerLayersSelector = state => state.map.tileServerLayers;
export const mapboxSourcesSelector = state => state.map.mapboxSources;
export const tileServerSourcesSelector = state => state.map.tileServerSources;
export const visibleMapboxSourcesSelector = state => state.map.visibleMapboxSources;
export const mapReadyForDrawingSelector = state => state.map.mapReadyForDrawing;
export const aloftSourcesSelector = state => state.map.aloftSources;
export const mapUnauthenticatedStyleSelector = state => state.map.unauthenticatedStyle;
export const mapClickedFeaturesSelector = state => state.map.clickedFeatures;
export const mapSearchOptionsSelector = state => state.map.searchOptions;
export const mapBoundingBoxSelector = state => state.map.mapBoundingBox;
export const renderMapSelector = state => state.map.renderMap;
export const showFeatureCardSelector = state => state.map.showFeatureCard;
export const savedMapViewsSelector = state => state.map.savedMapViews;
export const savedMapViewsLoadingSelector = state => state.map.savedMapViewsLoading;
export const mapPinInViewSelector = state => state.map.mapPinInView;
export const airspaceMapBoundsSelector = state => state.map.airspaceMapBounds;
export const airspaceMapPinSelector = state => state.map.airspaceMapPin;

export const mapBasemapSelector = state => state.map.basemap;
export const showPilotCardSelector = state => state.map.showPilotCard;
export const mapLayersSelector = state => state.map.mapLayers;
export const mapSourcesSelector = state => state.map.mapSources;
export const mapBasemapsSelector = state => state.map.mapBasemaps;
export const mapStylesSelector = state => state.map.mapStyles;
export const mapModifiersSelector = state => state.map.mapModifiers;
export const mapSectionsSelector = state => state.map.mapSections;
export const mapRegionsSelector = state => state.map.mapRegions;
export const geoJsonDataSelector = state => state.map.geoJsonData;
export const defaultBasemapSelector = state => state.map.defaultBasemap;
export const userLocationGeoJsonFeaturesSelector = state =>
	state.map.geoJsonData.user_location?.features || [];

export const userLocationFeaturesFilteredByMapBoundsSelector = createSelector(
	[userLocationGeoJsonFeaturesSelector, mapBoundingBoxSelector],
	(userLocationFeatures, mapBoundingBox) => {
		if (mapBoundingBox) {
			return filterMapFeaturesByBoundingBox({
				features: userLocationFeatures,
				boundingBox: mapBoundingBox
			});
		}
		return userLocationFeatures;
	}
);

// New selectors for map pin features
export const mapPinFeatureSelector = createSelector(
	[featureCollectionSelector],
	featureCollection => {
		if (!featureCollection?.features) return null;
		return featureCollection?.features?.find(feature => feature?.properties?.isMapPin === true);
	}
);

export const featureCollectionWithoutMapPinSelector = createSelector(
	[featureCollectionSelector],
	featureCollection => {
		if (!featureCollection?.features) return featureCollection;
		const featuresWithoutMapPin = featureCollection?.features?.filter(
			feature => feature?.properties?.isMapPin !== true
		);
		return {
			...featureCollection,
			features: featuresWithoutMapPin
		};
	}
);
export const clickedAdvisoryIdSelector = state => state.map.clickedAdvisoryId;

export const lineBufferFeetSelector = state =>
	state.map.featureCollection.features.find(f => f.properties?.isLineBuffer)?.properties
		?.bufferFeet;
